export class APIRequest {
    public static At(endpoint:string): APIRequestOptions {
        return new APIRequestOptions().setEndPoint(endpoint);
    }

    public static BaseOn(base:APIRequestOptions): APIRequestOptions {
        return APIRequestOptions.copyFrom(base);
    }
}

export type APIRequestAuthProvider = () => Promise<string>;

export class APIRequestResponse {
    successCallback: (response:any) => void = () => {};
    errorCallback: (context:any, response:string) => void = () => {};

    public onError(onError:(context:any, response:string) => void): APIRequestResponse {
        this.errorCallback = onError;
        return this;
    }

    public onSuccess(onSuccess:(response:any) => void): APIRequestResponse {
        this.successCallback = onSuccess;
        return this;
    }
}

export class APIRequestOptions {
    endpoint: string = "";
    method: string = "";
    contentType: string = "";
    body: any = "";
    endpointParams: { [key: string]: string } = {};

    authProvider: APIRequestAuthProvider | undefined;

    public setEndPoint(endpoint:string): APIRequestOptions {
        this.endpoint = endpoint;
        return this;
    }

    public setEndpointParam(key:string, value:string): APIRequestOptions {
        this.endpointParams[key] = value;
        return this;
    }

    public usePost(): APIRequestOptions {
        this.method = "POST";
        return this;
    }

    public useGet(): APIRequestOptions {
        this.method = "GET";
        return this;
    }

    public setBody(body:any): APIRequestOptions {
        this.body = body;
        return this;
    }

    public setContentType(contentType:string): APIRequestOptions {
        this.contentType = contentType;
        return this;
    }

    public setAuthProvider(authProvider:APIRequestAuthProvider): APIRequestOptions {
        this.authProvider = authProvider;
        return this;
    }  

    public send(): APIRequestResponse {
        const endpoint = APIRequestOptions.ResolveEndpoint(this.endpoint, this.endpointParams);
        const method = this.method;
        const body = JSON.stringify(this.body);
        const responseHandler = new APIRequestResponse();
    
        APIRequestOptions.BuildHeaders(this)
            .then((builtHeaders:any) => {
                const fetchOptions: RequestInit = {
                    method,
                    headers: builtHeaders
                };
    
                if (method !== "GET" && method !== "HEAD") {
                    fetchOptions.body = body;
                }
    
                fetch(endpoint, fetchOptions)
                    .then((response:Response) => {   
                        if (response.ok) {
                            if (APIRequestOptions.ResponseContainsJSON(response)) {
                                response.json().then((value:any) => {
                                    responseHandler.successCallback(value);
                                });
                            } else {
                                response.text().then((value:string) => {
                                    responseHandler.successCallback(value);
                                });
                            }
                        } else {
                            responseHandler.errorCallback(response, response.statusText);
                        }
                    })
                    .catch((exception:any) => {
                        responseHandler.errorCallback(exception, "Exception occurred");
                    });
            });
    
        return responseHandler;
    }

    public static copyFrom(base:APIRequestOptions): APIRequestOptions {
        const derrived = new APIRequestOptions();

        derrived.endpoint = base.endpoint;
        derrived.method = base.method;
        derrived.contentType = base.contentType;
        derrived.authProvider = base.authProvider;
        derrived.body = base.body;
        derrived.endpointParams = base.endpointParams;

        return derrived;
    }

    private static ResponseContainsJSON(response:Response): boolean {
        const contentType = response.headers.get("content-type");
        return contentType !== null && contentType.includes("application/json");
    }

    private static async BuildHeaders(options:APIRequestOptions): Promise<any>{
        const authProvision = options.authProvider ? await options.authProvider() : "";
        const authorization = authProvision.length > 0 ? authProvision: null;
        const contentType = options.contentType.length > 0 ? options.contentType : null;

        const header = {
            "Authorization": authorization,
            "Content-Type": contentType
        }

        return APIRequestOptions.SanitizeHeader(header);
    }

    private static SanitizeHeader(header:any): any{
        const newObj:any = {};

        for (const key in header) {
          if (header.hasOwnProperty(key) && header[key] !== null) {
            newObj[key] = header[key];
          }
        }

        return newObj;
    }

    private static ResolveEndpoint(endpoint: string, endpointParams: { [key: string]: string }): string {
        const matches = endpoint.match(/{.+}/);

        matches?.forEach(match => {
            const santizedMatch = match.substring(1, match.length-1);            
            endpoint = endpoint.replaceAll(match, endpointParams[santizedMatch]);
        })
        return endpoint;
    }
}